import * as yup from 'yup';
import React from 'react';
import { Trans } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { Platform, View } from 'react-native';

import { AppEnv } from '@components/AppEnv';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Logo } from '@components/Logo';
import { PublicScreenLayout } from '@components/PublicScreenLayout';
import { Typography } from '@components/Typography';
import { makeStyles } from '@providers/Theme';
import { useAuthStore } from '@stores/auth';
import { useTranslation } from '@providers/I18n';

const useStyles = makeStyles((theme) => ({
  root: {
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 0,
    ...Platform.select({
      web: {
        marginHorizontal: 'auto',
      }
    }),
    ...theme.mixins.padding(0, 3),
    on: {
      xs: {
        marginTop: theme.spacing(4),
      },
      md: {
        marginTop: 0,
        ...({
          web: {
            height: '100vh',
          }
        }),
      },
    },
  },
  text: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  button: {
    marginTop: theme.spacing(3),
  },
  error: {
    marginTop: theme.spacing(2),
  },
  logo: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  appEnv: {
    marginLeft: theme.spacing(1),
  },
}));

export type UserProps = {
  username: string;
  password: string;
};
export type ErrorType = 'invalid' | 'default';

export const LoginScreen: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { loading, error, login } = useAuthStore();

  const schema = React.useMemo(() => yup
  .object()
  .shape({
    username: yup.string().required(t('login.errors.username.required')),
    password: yup.string().required(t('login.errors.password.required')),
  }), [t]);

  const { control, handleSubmit } = useForm({
    defaultValues: {
      username: '',
      password: '',
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const onSubmit = async (userData: UserProps) => {
    await login(userData);
  };

  const errorMsg: Record<ErrorType, string> = {
    invalid: t('login.errors.invalid'),
    default: t('login.errors.default'),
  };

  const renderErrorMsg = () => {
    let message = '';
    if (
      error?.response?.status === 404 ||
      (error?.response?.status === 401 && error?.code === 'ERR_BAD_REQUEST')
    ) {
      message = errorMsg.invalid;
    } else {
      message = errorMsg.default;
    }

    return (
      <Typography
        testID="error-message"
        weight="light"
        size={{ xs: 'sm', lg: 'md' }}
        variant="error"
        style={styles.error}>
        {message}
      </Typography>
    );
  };

  return (
    <PublicScreenLayout>
      <View style={styles.root}>
        <View style={styles.logo}>
          <Logo height={50} testID="logo" />
          <AppEnv />
        </View>
        <Typography
          weight="semiBold"
          size={{ xs: '2xl', lg: '3xl' }}
          variant="primary"
          style={styles.text}
          testID="title">
          {t('login.title')}
        </Typography>
        <Typography
          weight="light"
          size={{ xs: 'md', lg: 'xl' }}
          variant="secondary"
          testID="subTitle">
          {t('login.subtitle')}
        </Typography>
        <Controller
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <Input
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              label={t('login.fields.username.title')}
              placeholder={t('login.fields.username.placeholder')}
              error={fieldState.error}
            />
          )}
          name="username"
        />
        <Controller
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <Input
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              label={t('login.fields.password.title')}
              placeholder={t('login.fields.password.placeholder')}
              error={fieldState.error}
              type="password"
            />
          )}
          name="password"
        />
        {error !== null && renderErrorMsg()}
        <Button
          variant="primary"
          onPress={handleSubmit(onSubmit, console.error)}
          fullWidth
          style={styles.button}
          loading={loading}>
          <Trans i18nKey="login.submit" />
        </Button>
      </View>
    </PublicScreenLayout>
  );
};
